import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useLocation, useSearchParams } from 'react-router-dom';
import { Form, Input, Button, Alert, Typography, Card, Space, Checkbox, Divider } from 'antd';
import { Notice } from '../common/Notice';
import { UserOutlined, LockOutlined, MailOutlined } from '@ant-design/icons';
import { Turnstile, TurnstileInstance } from '@marsidev/react-turnstile';
import { authService } from '../../services/auth.service';
import { marketingService } from '../../services/marketing.service';
import { getUserFriendlyMessage } from '../../utils/error-handler';
import { useAuth } from '../../hooks/useAuth';
import { GoogleSignInButton } from './GoogleSignInButton';
import {
  PASSWORD_POLICY_HINT,
  PASSWORD_POLICY_MESSAGE,
  PASSWORD_POLICY_REGEX,
} from '../../utils/password-policy';

const { Title, Paragraph, Text } = Typography;

function parseE164(value: string | undefined): { ddd: string; number: string } {
  if (!value || !value.startsWith('+55')) return { ddd: '', number: '' };
  const digits = value.slice(3);
  return { ddd: digits.slice(0, 2), number: digits.slice(2) };
}

function buildE164(ddd: string, number: string): string {
  const raw = ddd + number;
  return raw ? '+55' + raw : '';
}

function applyNumberMask(raw: string): string {
  if (raw.length <= 4) return raw;
  if (raw.length <= 8) return raw.slice(0, 4) + '-' + raw.slice(4);
  return raw.slice(0, 5) + '-' + raw.slice(5, 9);
}

interface PhoneInputProps {
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
}

const PhoneInput: React.FC<PhoneInputProps> = ({ value, onChange, disabled }) => {
  const parsed = parseE164(value);
  const [ddd, setDdd] = useState(parsed.ddd);
  const [number, setNumber] = useState(parsed.number);

  useEffect(() => {
    const p = parseE164(value);
    if (p.ddd !== ddd) setDdd(p.ddd);
    if (p.number !== number) setNumber(p.number);
  }, [value, ddd, number]);

  const handleDddChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '').slice(0, 2);
    setDdd(raw);
    onChange?.(buildE164(raw, number));
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '').slice(0, 9);
    setNumber(raw);
    onChange?.(buildE164(ddd, raw));
  };

  return (
    <Space.Compact style={{ width: '100%' }}>
      <Input
        value="🇧🇷 +55"
        readOnly
        disabled={disabled}
        size="large"
        tabIndex={-1}
        style={{ width: '90px', flexShrink: 0, cursor: 'default', color: 'rgba(0,0,0,0.88)' }}
      />
      <Input
        value={ddd}
        onChange={handleDddChange}
        placeholder="47"
        maxLength={2}
        disabled={disabled}
        size="large"
        inputMode="numeric"
        aria-label="DDD"
        style={{ width: '68px', flexShrink: 0 }}
      />
      <Input
        value={applyNumberMask(number)}
        onChange={handleNumberChange}
        placeholder="99999-9999"
        disabled={disabled}
        size="large"
        inputMode="numeric"
        aria-label="Número de telefone"
        style={{ flex: 1 }}
      />
    </Space.Compact>
  );
};

interface RegisterFormValues {
  name: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  termsOfService: boolean;
  privacyPolicy: boolean;
}

interface PhoneConflictState {
  email?: string;
  maskedOwnerEmail?: string;
  message: string;
}

export const RegisterForm: React.FC = () => {
  const [apiError, setApiError] = useState<string | null>(null);
  const [errorAction, setErrorAction] = useState<{ label: string; to: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const { signInWithGoogle } = useAuth();
  const [phoneConflict, setPhoneConflict] = useState<PhoneConflictState | null>(null);
  const [transferSessionId, setTransferSessionId] = useState<number | null>(null);
  const [transferOtpCode, setTransferOtpCode] = useState('');
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileInstance>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [form] = Form.useForm<RegisterFormValues>();
  const participationSurveyIdFromState =
    (location.state as { participationSurveyId?: string } | undefined)?.participationSurveyId;
  const participationSurveyId = participationSurveyIdFromState || searchParams.get('surveyId') || undefined;
  const referralCode = searchParams.get('ref') ?? undefined;
  const redirectParam = searchParams.get('redirect') ?? undefined;
  const prefillName = (location.state as { prefillName?: string } | null)?.prefillName;
  const prefillEmail = (location.state as { prefillEmail?: string } | null)?.prefillEmail;

  useEffect(() => {
    if (prefillName || prefillEmail) {
      form.setFieldsValue({
        ...(prefillName ? { name: prefillName } : {}),
        ...(prefillEmail ? { email: prefillEmail } : {}),
      });
    }
  }, [form, prefillName, prefillEmail]);

  const clearTransferState = () => {
    setPhoneConflict(null);
    setTransferSessionId(null);
    setTransferOtpCode('');
  };

  const handleSubmit = async (values: RegisterFormValues) => {
    if (!captchaToken) {
      setApiError('Por favor, complete a verificação de segurança.');
      return;
    }

    setApiError(null);
    setErrorAction(null);
    clearTransferState();
    setLoading(true);

    try {
      const registerResult = await authService.register({
        email: values.email,
        password: values.password,
        phone: values.phone,
        name: values.name,
        referralCode,
        captchaToken,
        legalConsents: {
          termsOfService: values.termsOfService,
          privacyPolicy: values.privacyPolicy,
        },
      });
      const userId = String(registerResult?.userId ?? '');
      marketingService.pushEvent('cadastro_criado', { user_id: userId });
      navigate('/confirm-account', {
        state: {
          email: values.email,
          participationSurveyId,
          userId,
          redirect: redirectParam,
        },
      });
    } catch (err: unknown) {
      const httpError = err as {
        response?: {
          status?: number;
          data?: {
            action?: string;
            error?: string;
            email?: string;
            maskedOwnerEmail?: string;
            message?: string;
          };
        };
      };
      const status = httpError.response?.status;
      const data = httpError.response?.data;

      if (status === 409 && data?.action === 'FORGOT_PASSWORD') {
        navigate('/forgot-password', { state: { email: values.email } });
        return;
      }

      if ((status === 409 || status === 422) && (data?.error === 'PHONE_ALREADY_CLAIMED' || data?.email)) {
        setApiError(null);
        setErrorAction(null);
        setPhoneConflict({
          email: data?.email,
          maskedOwnerEmail: data?.maskedOwnerEmail,
          message:
            data?.message ||
            'Este telefone já está associado a outra conta. Voce pode usar outro numero, recuperar acesso ou validar mesmo assim.',
        });
        return;
      }

      const errorMessage = getUserFriendlyMessage(err);
      setApiError(errorMessage);
      turnstileRef.current?.reset();
      setCaptchaToken(null);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleCredential = async (idToken: string) => {
    setApiError(null);
    setErrorAction(null);
    setGoogleLoading(true);

    try {
      const { isNewAccount } = await signInWithGoogle(idToken, referralCode);

      if (isNewAccount) {
        marketingService.pushEvent('cadastro_criado', { auth_provider: 'google' });
        // FR-006/US4: telefone e demais dados ficam pendentes — guia o usuário
        // para a etapa de complementação de cadastro já usada hoje.
        navigate('/profile', {
          state: {
            message: 'Conta criada com Google! Complete a verificação do seu telefone para liberar todas as funcionalidades.',
          },
        });
        return;
      }

      navigate(redirectParam && redirectParam.startsWith('/') ? redirectParam : '/');
    } catch (err: unknown) {
      const httpError = err as { response?: { data?: { message?: string } } };
      setApiError(httpError.response?.data?.message || getUserFriendlyMessage(err));
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleUseAnotherPhone = () => {
    clearTransferState();
    form.setFieldsValue({ phone: '' });
  };

  const handleRecoverAccess = () => {
    const emailHint = phoneConflict?.email || form.getFieldValue('email');
    navigate('/forgot-password', {
      state: {
        email: emailHint,
        fromRegister: true,
      },
    });
  };

  const handleStartTransfer = async () => {
    const values = form.getFieldsValue(true);
    if (!values.email || !values.password || !values.phone) {
      setApiError('Preencha email, telefone e senha para iniciar a transferência.');
      return;
    }

    setApiError(null);
    setErrorAction(null);
    setLoading(true);
    try {
      const transfer = await authService.startRegisterPhoneTransfer({
        email: values.email,
        password: values.password,
        phone: values.phone,
        name: values.name,
        referralCode,
        legalConsents: {
          termsOfService: !!values.termsOfService,
          privacyPolicy: !!values.privacyPolicy,
        },
      });

      setTransferSessionId(transfer.transferSessionId);
      setApiError(null);
    } catch (err: unknown) {
      const errorMessage = getUserFriendlyMessage(err);
      setApiError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyTransfer = async () => {
    const values = form.getFieldsValue(true);

    if (!transferSessionId) {
      setApiError('Sessão de transferência não iniciada.');
      return;
    }

    if (!transferOtpCode) {
      setApiError('Informe o código OTP recebido por SMS.');
      return;
    }

    setApiError(null);
    setLoading(true);

    try {
      const transferResult = await authService.verifyRegisterPhoneTransfer({
        email: values.email,
        transferSessionId,
        otpCode: transferOtpCode,
      });
      const userId = String(transferResult?.userId ?? '');
      marketingService.pushEvent('cadastro_criado', { user_id: userId });

      clearTransferState();
      navigate('/confirm-account', {
        state: {
          email: values.email,
          participationSurveyId,
          userId,
          redirect: redirectParam,
        },
      });
    } catch (err: unknown) {
      const errorMessage = getUserFriendlyMessage(err);
      setApiError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 500, margin: '80px auto', padding: '0 20px' }}>
      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Link to="/" style={{ display: 'flex', justifyContent: 'center' }}>
            <img src="/assets/brand/logo.png" alt="PePoll" style={{ height: 36 }} />
          </Link>

          <Title level={2} style={{ textAlign: 'center', marginBottom: 0 }}>
            Criar Conta
          </Title>

          <Paragraph type="secondary" style={{ textAlign: 'center', marginTop: 0 }}>
            Cadastre-se para participar de pesquisas
          </Paragraph>

          {apiError && (
            <Alert
              message="Erro ao criar conta"
              description={
                <div>
                  {apiError}
                  {errorAction && (
                    <div style={{ marginTop: 8 }}>
                      <Link to={errorAction.to}>{errorAction.label}</Link>
                    </div>
                  )}
                </div>
              }
              type="error"
              showIcon
              closable
              onClose={() => {
                setApiError(null);
                setErrorAction(null);
              }}
            />
          )}

          {phoneConflict && (
            <Notice
              message="Telefone em conflito"
              description={
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <Text>
                    {phoneConflict.message}
                    {phoneConflict.maskedOwnerEmail ? ` (${phoneConflict.maskedOwnerEmail}).` : ''}
                  </Text>
                  <Space wrap>
                    <Button size="small" onClick={handleUseAnotherPhone} disabled={loading}>
                      Usar outro número
                    </Button>
                    <Button size="small" onClick={handleRecoverAccess} disabled={loading}>
                      Recuperar acesso
                    </Button>
                    <Button
                      size="small"
                      type="primary"
                      onClick={handleStartTransfer}
                      loading={loading}
                    >
                      Validar mesmo assim
                    </Button>
                  </Space>
                  {transferSessionId && (
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Text type="secondary">
                        Informe o código recebido por SMS para concluir a transferência deste telefone.
                      </Text>
                      <Input
                        value={transferOtpCode}
                        onChange={(e) => setTransferOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="Código OTP"
                        maxLength={6}
                        disabled={loading}
                        size="large"
                      />
                      <Button
                        type="primary"
                        onClick={handleVerifyTransfer}
                        loading={loading}
                        disabled={transferOtpCode.length < 6}
                      >
                        Confirmar transferência e continuar cadastro
                      </Button>
                    </Space>
                  )}
                </Space>
              }
              type="warning"
              showIcon
              closable
              onClose={() => clearTransferState()}
            />
          )}

          <GoogleSignInButton onCredential={handleGoogleCredential} disabled={loading} loading={googleLoading} />

          <Divider plain style={{ margin: 0, color: '#595959', fontSize: 14, fontWeight: 400 }}>
            ou cadastre-se com e-mail
          </Divider>

          <Form
            form={form}
            name="register"
            onFinish={handleSubmit}
            layout="vertical"
            autoComplete="off"
            disabled={googleLoading}
          >
            <Form.Item
              label="Nome"
              name="name"
              rules={[
                { min: 2, message: 'Nome deve ter pelo menos 2 caracteres' },
              ]}
              extra="Seu nome completo (opcional)"
            >
              <Input
                prefix={<UserOutlined />}
                placeholder="João Silva"
                size="large"
                disabled={loading}
              />
            </Form.Item>

            <Form.Item
              label="Email"
              name="email"
              rules={[
                { required: true, message: 'Por favor, insira seu email!' },
                { type: 'email', message: 'Email inválido!' },
              ]}
              extra="Você receberá um link de confirmação"
            >
              <Input
                prefix={<MailOutlined />}
                placeholder="seu@email.com"
                size="large"
                disabled={loading}
              />
            </Form.Item>

            <Form.Item
              label="Telefone"
              name="phone"
              rules={[
                { required: true, message: 'Por favor, insira seu telefone!' },
                {
                  pattern: /^\+55\d{2}\d{8,9}$/,
                  message: 'Telefone inválido. Informe DDD + 8 ou 9 dígitos.',
                },
              ]}
              extra="DDD + número com 8 ou 9 dígitos. Ex: 47 + 99999-9999"
            >
              <PhoneInput disabled={loading} />
            </Form.Item>

            <Form.Item
              label="Senha"
              name="password"
              rules={[
                { required: true, message: 'Por favor, insira sua senha!' },
                { min: 8, message: 'Senha deve ter no mínimo 8 caracteres!' },
                { pattern: PASSWORD_POLICY_REGEX, message: PASSWORD_POLICY_MESSAGE },
              ]}
              extra={PASSWORD_POLICY_HINT}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="••••••••"
                size="large"
                disabled={loading}
              />
            </Form.Item>

            <Form.Item
              label="Confirmar Senha"
              name="confirmPassword"
              dependencies={['password']}
              rules={[
                { required: true, message: 'Por favor, confirme sua senha!' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('As senhas não coincidem!'));
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="••••••••"
                size="large"
                disabled={loading}
              />
            </Form.Item>

            <Form.Item
              name="termsOfService"
              valuePropName="checked"
              rules={[
                {
                  validator: (_, value) =>
                    value
                      ? Promise.resolve()
                      : Promise.reject(new Error('Você deve aceitar os Termos de Uso para continuar.')),
                },
              ]}
            >
              <Checkbox disabled={loading}>
                Li e aceito os{' '}
                <Link to="/legal/termos-de-uso" target="_blank" rel="noopener noreferrer">
                  Termos de Uso
                </Link>
              </Checkbox>
            </Form.Item>

            <Form.Item
              name="privacyPolicy"
              valuePropName="checked"
              rules={[
                {
                  validator: (_, value) =>
                    value
                      ? Promise.resolve()
                      : Promise.reject(new Error('Você deve aceitar a Política de Privacidade para continuar.')),
                },
              ]}
            >
              <Checkbox disabled={loading}>
                Li e aceito a{' '}
                <Link to="/legal/politica-de-privacidade" target="_blank" rel="noopener noreferrer">
                  Política de Privacidade
                </Link>
              </Checkbox>
            </Form.Item>

            <Form.Item style={{ marginBottom: 8 }}>
              <Turnstile
                ref={turnstileRef}
                siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
                onSuccess={(token) => setCaptchaToken(token)}
                onExpire={() => setCaptchaToken(null)}
                onError={() => setCaptchaToken(null)}
                options={{ theme: 'light' }}
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0 }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                size="large"
                block
                disabled={!captchaToken || googleLoading}
              >
                Criar Conta
              </Button>
            </Form.Item>
          </Form>

          <div style={{ textAlign: 'center' }}>
            <Text type="secondary">
              Já tem conta? <Link to="/login">Entrar</Link>
            </Text>
          </div>
        </Space>
      </Card>
    </div>
  );
};